import { z } from 'zod';
import { MediaType } from '../enums.js';

/** Presigned upload request (spec section 10). */
export const presignUploadSchema = z.object({
  filename: z.string().min(1).max(240),
  contentType: z.string().min(3).max(120),
  sizeBytes: z.number().int().min(1).max(26_214_400), // 25 MiB default cap
  type: z.enum([MediaType.IMAGE, MediaType.VIDEO, MediaType.DOWNLOADABLE]),
  // Where this asset will be used, drives object-key prefix + visibility
  usage: z.enum([
    'product',
    'variant',
    'banner',
    'content',
    'category',
    'collection',
    'custom-order',
  ]),
  ownerId: z.string().uuid().optional(),
  isPrivate: z.boolean().default(false),
});

/** Finalize: browser tells the backend the S3 upload completed. */
export const finalizeUploadSchema = z.object({
  mediaId: z.string().uuid(),
  checksum: z.string().max(128).optional(),
});

export const allowedImageTypes = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/gif',
] as const;

export const allowedVideoTypes = ['video/mp4', 'video/webm'] as const;

export type PresignUploadInput = z.infer<typeof presignUploadSchema>;

/**
 * Product pictures are cropped to this square on upload (admin) and shown in
 * square frames on the product page and product cards. 1200px stays sharp on
 * retina at the largest size the product page displays.
 */
export const PRODUCT_IMAGE_SIZE = { width: 1200, height: 1200 } as const;
